import type { ViewportLayout } from '../constants/viewport'
import { create } from 'zustand'

/**
 * Estado de presentación responsive.
 * Aislado del store de escena (`useAppStore`): no contiene items, texturas,
 * historial ni datos de cámara. Solo chrome UI.
 *
 * Correspondencia: ATEMPORAL mobile-28-07-2026 (`47ee2081`).
 * Adaptación KUBE: sin sheet `options` (no hay ArmsCushionsPanel).
 */
export type ActiveSheet = 'modules' | 'textures' | 'more' | null

/** Modo de chrome táctil (no confundir con `mode` de escena en useAppStore). */
export type ChromeEditMode = 'navigate' | 'select' | 'configure' | 'texture'

type UiPresentationState = {
  /** Layout derivado del viewport (sincronizado por useSyncViewportLayout). */
  layout: ViewportLayout
  /** Sheet / drawer activo en chromes compactos/táctiles. */
  activeSheet: ActiveSheet
  /** Chrome visible (auto-hide futuro). */
  chromeVisible: boolean
  /** Canvas a pantalla completa (oculta chrome no esencial). */
  canvasFullscreen: boolean
  editMode: ChromeEditMode
  overflowOpen: boolean

  setLayout: (layout: ViewportLayout) => void
  setActiveSheet: (sheet: ActiveSheet) => void
  openSheet: (sheet: Exclude<ActiveSheet, null>) => void
  closeSheet: () => void
  toggleSheet: (sheet: Exclude<ActiveSheet, null>) => void
  setChromeVisible: (visible: boolean) => void
  setCanvasFullscreen: (fullscreen: boolean) => void
  setEditMode: (mode: ChromeEditMode) => void
  setOverflowOpen: (open: boolean) => void
  resetPresentation: () => void
}

const initialPresentation = {
  layout: 'desktop' as ViewportLayout,
  activeSheet: null as ActiveSheet,
  chromeVisible: true,
  canvasFullscreen: false,
  editMode: 'navigate' as ChromeEditMode,
  overflowOpen: false,
}

export const useUiPresentationStore = create<UiPresentationState>((set, get) => ({
  ...initialPresentation,

  setLayout: (layout) => {
    if (get().layout === layout) return
    set({ layout })
  },

  setActiveSheet: (activeSheet) => set({ activeSheet, overflowOpen: false }),

  openSheet: (sheet) =>
    set({
      activeSheet: sheet,
      canvasFullscreen: false,
      chromeVisible: true,
      overflowOpen: false,
    }),

  closeSheet: () => set({ activeSheet: null }),

  toggleSheet: (sheet) => {
    const current = get().activeSheet
    set({
      activeSheet: current === sheet ? null : sheet,
      overflowOpen: false,
      canvasFullscreen: false,
      chromeVisible: true,
    })
  },

  setChromeVisible: (chromeVisible) => set({ chromeVisible }),

  setCanvasFullscreen: (canvasFullscreen) =>
    set({
      canvasFullscreen,
      activeSheet: canvasFullscreen ? null : get().activeSheet,
      overflowOpen: false,
    }),

  setEditMode: (editMode) => set({ editMode }),

  setOverflowOpen: (overflowOpen) => set({ overflowOpen }),

  resetPresentation: () =>
    set({
      ...initialPresentation,
      layout: get().layout,
    }),
}))
